/**
 * Operator portal rework PR 5 — `/portal/view-as#code=…`, where web-admin's "View as" lands.
 * The code is in the fragment so it never reaches a server log or a Referer header.
 *
 * Exchanges the one-time code (POST /v1/view-as/exchange, good once and for two minutes) for a
 * session token, starts the view in this tab (auth/view-as.ts) and opens the portal home. The
 * code is dropped from the address bar first, so it is not left in history.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { beginViewAs, type ViewAsSession } from '../auth/view-as';
import { publicPost } from './publicApi';

export function ViewAsStartPage(): React.JSX.Element {
  const { hash } = useLocation();
  const code = new URLSearchParams(hash.replace(/^#/, '')).get('code');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [failed, setFailed] = useState(false);
  // StrictMode runs effects twice; the code is good once.
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current || code === null || code === '') return;
    firedRef.current = true;
    window.history.replaceState(null, '', '/portal/view-as');
    void (async (): Promise<void> => {
      try {
        const session = await publicPost<{ code: string }, ViewAsSession>('/v1/view-as/exchange', {
          code,
        });
        // Whatever this tab showed before belongs to someone else.
        queryClient.clear();
        beginViewAs(session);
        void navigate('/portal/operator', { replace: true });
      } catch {
        setFailed(true);
      }
    })();
  }, [code, navigate, queryClient]);

  if (code === null || code === '' || failed) {
    return (
      <div>
        <h1
          style={{
            fontSize: '22px',
            fontWeight: 700,
            marginBottom: '12px',
            color: 'var(--color-danger)',
          }}
        >
          This view-as link has expired
        </h1>
        <p style={{ fontSize: '16px', color: 'var(--color-text-secondary)' }}>
          A view-as link works once, within two minutes. Start a new one from the admin console.
        </p>
      </div>
    );
  }
  return (
    <p role="status" style={{ fontSize: '16px', color: 'var(--color-text-secondary)' }}>
      Opening the portal…
    </p>
  );
}

/** Shown in place of the sign-in page once a view-as session has ended in this tab. */
export function ViewAsEndedPage(): React.JSX.Element {
  return (
    <div>
      <h1 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '12px' }}>
        The view-as session has ended
      </h1>
      <p style={{ fontSize: '16px', color: 'var(--color-text-secondary)' }}>
        You can close this tab. To look again, start a new view from the admin console.
      </p>
    </div>
  );
}
